import { Link } from "react-router-dom";
import { ArrowRight, Clock, CircleDollarSign, ShieldCheck, Gauge } from "lucide-react";
import { StatusRow, toneFromRag, MilestoneStepper } from "@/design/components";
import { useSeed, useMoney, useProjects } from "@/domain/hooks";
import { currentMilestoneId } from "@/domain/status";
import { formatPct } from "@/design/format";

/**
 * Delivery and disbursement panel: where a project sits against its milestone gates,
 * and how ready the current milestone is for its next disbursement (on time, on budget,
 * well evidenced). Reused on the Stratus dashboard and the project page.
 */
export function DeliveryProgress({
  projectId,
  approvalsHref = "/stratus/approvals",
  showHeaderLink = true,
  showCta = true,
  showProjectName = true,
}: {
  projectId?: string;
  approvalsHref?: string;
  showHeaderLink?: boolean;
  showCta?: boolean;
  /** When the project name already heads the page, hide it here so it appears once. */
  showProjectName?: boolean;
}) {
  const seed = useSeed();
  const money = useMoney();
  // useProjects(), not seed.projects: seed is always the demo/fixture blob
  // (hydrated regardless of mode), whose project ids are fake slugs. A real
  // numeric projectId (e.g. from ProjectManagementView.tsx's route param)
  // never matched anything in seed.projects, so `project` came back
  // undefined and this whole panel silently vanished on a real project
  // page. Known, separate follow-up: seed.milestones just below is still
  // demo-sourced, so the milestone track itself may still read empty for a
  // real project even with this fix - milestones need the same real-vs-demo
  // treatment in a future pass; this fix restores the panel, not that data.
  const realProjects = useProjects();
  const project = projectId
    ? realProjects.find((p) => p.id === projectId)
    : realProjects.find((p) => p.spent > 0) ?? realProjects[0];
  if (!project) return null;

  // The project management strip shows the project-level milestone sequence (spec §3.3);
  // sub-project milestones live on the sub-project's own surface, so they are not flattened
  // in here.
  const ms = seed.milestones.filter((m) => m.projectId === project.id && !m.subProjectId);
  const total = ms.length;
  const disbursedCount = ms.filter((m) => m.status === "disbursed" || m.status === "closed").length;
  // Current = the earliest still-open gate in this track (derived, one per track).
  const currentId = currentMilestoneId(ms);
  const byId = ms.findIndex((m) => m.id === currentId);
  const currentIdx = Math.max(0, byId >= 0 ? byId : disbursedCount);
  const current = ms[currentIdx];
  const completion = total ? disbursedCount / total : 0;

  const disbFor = (milestoneId: string) => seed.disbursements.find((d) => d.milestoneId === milestoneId);
  const tranche = (m: (typeof ms)[number]) => disbFor(m.id)?.amount ?? Math.round(m.budgetSharePct * project.budgeted);
  const nextDisb = current ? disbFor(current.id) : undefined;

  const evidencedPct = project.spent ? project.evidenced / project.spent : 0;
  const plannedPct = ms.slice(0, currentIdx + 1).reduce((a, m) => a + m.budgetSharePct, 0);
  const spentPct = project.budgeted ? project.spent / project.budgeted : 0;
  const drift = project.scheduleDriftDays ?? 0;

  const timeRag = drift > 10 ? "RED" : drift > 5 ? "AMBER" : "GREEN";
  const budgetRatio = plannedPct ? spentPct / plannedPct : 1;
  const evidenceTarget = 0.9; // the disbursement gate's evidence threshold

  const gateMessage =
    nextDisb?.state === "ready_for_funder"
      ? "Routed to the Danish Impact Fund. Awaiting the funder's approval to release the tranche and unlock the next milestone."
      : nextDisb?.state === "queued_for_stratus"
      ? "Evidence pack received. Review and route to the Danish Impact Fund to release the tranche."
      : `${formatPct(1 - evidencedPct)} of spend still to be evidenced before this milestone reaches its disbursement gate.`;

  return (
    <section className="overflow-hidden rounded-xl border border-line bg-card">
      <div className="h-1 w-full bg-brand" aria-hidden />
      <div className="flex flex-wrap items-start justify-between gap-3 px-5 pt-4">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brandSoft text-brand">
            <Gauge className="h-4 w-4" />
          </span>
          <div>
            <h2 className="text-sm font-semibold text-ink">Delivery and disbursement</h2>
            <p className="text-xs text-faint">
              {showProjectName ? `${project.name}${projectId ? "" : " · lead site"}` : `${disbursedCount} of ${total} milestones disbursed`}
            </p>
          </div>
        </div>
        {showHeaderLink && (
          <Link to={approvalsHref} className="flex items-center gap-1 text-xs font-medium text-brand hover:underline">
            Review disbursements <ArrowRight className="h-3 w-3" />
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 p-5 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <div className="flex items-end gap-3">
            <span className="text-4xl font-semibold tabular-nums leading-none text-ink">{disbursedCount}</span>
            <span className="pb-0.5 text-sm text-mute">of {total} milestones disbursed</span>
            <span className="ml-auto pb-0.5 text-sm font-medium tabular-nums text-mute">{formatPct(completion)} complete</span>
          </div>

          <div className="mt-5">
            <MilestoneStepper milestones={ms} driftDays={drift} renderAmount={(m) => money(tranche(m))} />
          </div>
        </div>

        <div className="rounded-lg border border-line bg-subtle/40 p-4 lg:col-span-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-faint">Next disbursement gate</p>
          <div className="mt-1 flex items-center justify-between">
            <p className="text-sm font-semibold text-ink">{current?.name ?? "—"}</p>
            <p className="text-base font-semibold tabular-nums text-ink">{current ? money(tranche(current)) : "—"}</p>
          </div>

          {/* Law 1: labels are neutral nouns; the value and colour carry the state. */}
          <div className="mt-3 space-y-2.5">
            <StatusRow
              icon={Clock}
              label="Schedule"
              tone={toneFromRag(timeRag)}
              value={drift <= 0 ? `${Math.abs(drift)} days ahead` : `${drift} days behind`}
              fill={timeRag === "GREEN" ? 0.9 : timeRag === "AMBER" ? 0.6 : 0.3}
            />
            <StatusRow
              icon={CircleDollarSign}
              label="Budget"
              tone={budgetRatio > 1.1 ? "danger" : "info"}
              value={`${formatPct(spentPct)} spent`}
              fill={Math.min(1, spentPct)}
            />
            <StatusRow
              icon={ShieldCheck}
              label="Evidence"
              tone={evidencedPct >= evidenceTarget ? "success" : "warn"}
              value={`${formatPct(evidencedPct)} complete · target ${formatPct(evidenceTarget)}`}
              fill={evidencedPct}
              target={evidenceTarget}
            />
          </div>

          <p className="mt-3 text-xs leading-snug text-mute">{gateMessage}</p>
          {showCta && (
            <Link
              to={approvalsHref}
              className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-brand px-3 py-2 text-xs font-medium text-white hover:opacity-90"
            >
              {nextDisb?.state === "queued_for_stratus" ? "Route to funder" : "Open in approvals"} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

